import React, {
  useRef,
  useLayoutEffect,
  forwardRef,
  useImperativeHandle,
  ReactNode,
} from "react";
import gsap from "gsap";
import styled, { CSSProperties } from "styled-components";
import ScrollTrigger from "gsap/ScrollTrigger";
import DOMPurify from "dompurify";

gsap.registerPlugin(ScrollTrigger);

/* eslint-disable-next-line */
export interface TextProps {
  label?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";
  figmaStyle: string;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  gradient?: {
    colors: {
      color: string;
      offset?: string;
    }[];
    angle?: number;
  };
  visuallyhidden?: boolean;

  /**
   * @param formatParam 设置文字的格式化参数。
   * @param strongCharacter 设置重点文字的前后符号，默认为 "{" 和 "}" 。
   * @param strongClassName 设置重点文字生成的 strong 标签的 className。
   * @param wrapCharacter 设置换行符，默认为 "_"。
   */
  formatParam?: {
    strongCharacter?: { left: string; right: string };
    strongClassName?: string;
    wrapCharacter?: string;
  };
}

interface TextFormatParamProps {
  /**
   * @param strongCharacter 设置重点文字的前后符号，默认为 "{" 和 "}" 。
   * @param strongClassName 设置重点文字生成的 strong 标签的 className。
   * @param wrapCharacter 设置换行符，默认为 "_"。
   */
  strongCharacter?: { left: string; right: string };
  strongClassName?: string;
  wrapCharacter?: string;
}

interface StyledTextProps {
  figmaStyle: string;

  gradient?: {
    colors: {
      color: string;
      offset?: string;
    }[];
    angle?: number;
  };
}

export interface TextHandle {
  getDOMNode: () =>
    | HTMLHeadingElement
    | HTMLParagraphElement
    | HTMLSpanElement
    | null;
  seeHello: () => void;
}

const getGradientStyle = (
  gradient:
    | {
        colors: {
          color: string;
          offset?: string;
        }[];
        angle?: number;
      }
    | undefined,
  figmaStyle: string
): CSSProperties | undefined => {
  if (gradient) {
    const style: CSSProperties = {
      background: getBackground(gradient, figmaStyle),
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    };

    return {
      ...style,
      // ...getGradientColorAndOffset(gradient, componentName),
    };
  }
};

const getBackground = (
  gradient:
    | {
        colors: {
          color: string;
          offset?: string;
        }[];
        angle?: number;
      }
    | undefined,
  figmaStyle: string
) => {
  return gradient
    ? `linear-gradient(${
        gradient.angle === undefined || gradient.angle === null
          ? 90
          : gradient.angle
      }deg, ${gradient.colors
        .map(
          (item, index, array) =>
            `var(--detail-${figmaStyle}-color-${index + 1},${
              item.color
            }) var(--detail-${figmaStyle}-color-offset-${index + 1},${
              item.offset
                ? item.offset
                : `${(index * 100) / (array.length - 1)}%`
            })`
        )
        .join(",")})`
    : "none";
};

const visuallyhiddenStyle: React.CSSProperties = {
  position: "absolute",
  clip: "rect(1px,1px,1px,1px)",
  clipPath: "inset(0px 0px 99.9% 99.9%)",
  overflow: "hidden",
  height: "1px",
  width: "1px",
  padding: "0px",
  border: "0px",
  margin: "0px",
};

const StyledText = styled.div<StyledTextProps>((props) => ({
  margin: 0,
  fontSize: `var(--detail-${props.figmaStyle}-font-size,var(--detail-font-size))`,
  fontFamily: `var(--detail-${props.figmaStyle}-font-family,var(--detail-font-family))`,
  fontStyle: `var(--detail-${props.figmaStyle}-font-style,var(--detail-font-style))`,
  fontWeight: `var(--detail-${props.figmaStyle}-font-weight,var(--detail-font-weight))`,
  lineHeight: `var(--detail-${props.figmaStyle}-line-height,var(--detail-line-height))`,
  color: ` var(--detail-${props.figmaStyle}-color,var(--detail-color))`,
  ...getGradientStyle(props.gradient, props.figmaStyle),
}));

function formatText(text: string, format: TextFormatParamProps): string {
  const {
    strongCharacter = { left: "{", right: "}" },
    wrapCharacter = "_",
  } = format;

  if (
    text.indexOf(strongCharacter.left) < 0 &&
    text.indexOf(wrapCharacter) < 0
  ) {
    return text;
  } else if (text.indexOf(strongCharacter.left) >= 0) {
    const rightBrackets = text.indexOf(strongCharacter.left);
    const leftBrackets = text.indexOf(strongCharacter.right) + 1;
    const oldText = text.slice(rightBrackets, leftBrackets);
    const newText = `<strong class="CLASSNAME">${text.slice(
      rightBrackets + 1,
      leftBrackets - 1
    )}</strong>`;

    return formatText(text.replace(oldText, newText), format);
  } else {
    const brackets = text.indexOf(wrapCharacter);
    const oldText = text.slice(brackets, brackets + 1);
    const newText = `<br />`;
    return formatText(text.replace(oldText, newText), format);
  }
}

const renderNode = (
  textString: string,
  visuallyhiddenProp?: boolean,
  format: TextFormatParamProps = {
    strongCharacter: { left: "{", right: "}" },
    strongClassName: "",
    wrapCharacter: "_",
  }
): string => {
  const { strongClassName = "" } = format;
  if (visuallyhiddenProp) {
    return `<span style="position: absolute;
    clip: rect(1px,1px,1px,1px);
    clipPath: inset(0px 0px 99.9% 99.9%);
    overflow: hidden;
    height: 1px;
    width: 1px;
    padding: 0px;
    border: 0px;
    margin: 0px">${formatText(textString, format)?.replace(
      /CLASSNAME/g,
      strongClassName
    )}</span>`;
  } else {
    return formatText(textString, format)?.replace(
      /CLASSNAME/g,
      strongClassName
    );
  }
};

export const Text = forwardRef<TextHandle, TextProps>((props, ref) => {
  const {
    label = "p",
    figmaStyle,
    children,
    className,
    style,
    gradient,
    visuallyhidden,
    formatParam,
  } = props;

  const innerRef = useRef<
    HTMLHeadingElement | HTMLParagraphElement | HTMLSpanElement
  >(null);

  useImperativeHandle(ref, () => ({
    getDOMNode: () => innerRef.current,
    seeHello: () => {
      console.log("Hello,World!");
    },
  }));

  return typeof children === "string" ? (
    <StyledText
      className={`detail-${figmaStyle} ${className ? className : ""}`}
      ref={innerRef}
      style={style}
      gradient={gradient}
      as={label}
      figmaStyle={figmaStyle}
      dangerouslySetInnerHTML={{
        //需要判断 children 的类型是 string 还是 React 元素
        __html: renderNode(children, visuallyhidden, formatParam),
      }}
    ></StyledText>
  ) : (
    <StyledText
      className={`detail-${figmaStyle} ${className ? className : ""}`}
      ref={innerRef}
      style={style}
      gradient={gradient}
      as={label}
      figmaStyle={figmaStyle}
    >
      {visuallyhidden ? (
        <span style={visuallyhiddenStyle}>{children}</span>
      ) : (
        children
      )}
    </StyledText>
  );
});

export default Text;
